import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { listProjects } from '@apis/projects';
import { selectWorkingSite } from '@store/selectors/site.selectors';
import { setWorkingSiteSuccess } from '@store/slices/site.slice';
import { routes } from '@src/routes';

/**
 * The path before `/:siteId` of every route that carries one (`/monitor`,
 * `/settings/alerts`), derived from the route table rather than listed by hand.
 *
 * This used to be a hardcoded trio (monitor / dashboard / cad) while fourteen
 * sections actually take a `:siteId`. Any page left off the list renders with
 * whatever site was selected LAST — so a deep link showed one site's data dated
 * in another site's timezone, silently. Deriving it means a new site-scoped
 * route is covered the moment it is added — including one nested a level down,
 * whose `:siteId` is not the second segment.
 */
const SITE_PREFIXES: readonly string[] = [
  ...new Set(
    routes
      .map((r) => r.path ?? '')
      .filter((p) => p.includes('/:siteId'))
      .map((p) => p.slice(0, p.indexOf('/:siteId'))),
  ),
];

/** The `:siteId` a pathname names, or undefined when no site-scoped route matches it. */
export function siteIdFromPath(pathname: string): string | undefined {
  for (const prefix of SITE_PREFIXES) {
    const match = matchPath(`${prefix}/:siteId/*`, pathname);
    if (match) return match.params.siteId;
  }
  return undefined;
}

/**
 * Reconciles `localStorage.workingSite` with the URL `:siteId` on mount.
 * If the URL site doesn't match the stored working site (e.g. direct page
 * load, browser back to a different monitor view), refetches the projects
 * list and dispatches `setWorkingSiteSuccess` for the matching entry.
 * No-ops when the URL and stored site already agree.
 *
 * Implementation note: this hook is mounted inside `Shell` (App.tsx), which
 * sits above `<Routes>` rather than inside one of the matched routes. Using
 * `useParams` here would return empty (params belong to the matched child
 * route), so we resolve `:siteId` by matching the pathname against the
 * patterns that carry it — which also keeps `/connect/:provider` and
 * `/settings/security` from being read as a site.
 */
export function useEnsureWorkingSite(): void {
  const siteId = siteIdFromPath(useLocation().pathname);

  const dispatch = useDispatch();
  const workingSite = useSelector(selectWorkingSite);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!siteId) return;
    if (workingSite?.siteId === siteId) return;

    let cancelled = false;
    (async () => {
      // Share the cache slot with ProjectSelector's dropdown listing so this
      // reconcile-on-mount path reuses an in-flight or recent fetch instead of
      // issuing a parallel duplicate request.
      const projects = await queryClient.fetchQuery({
        queryKey: ['projects', 'list'] as const,
        queryFn: ({ signal }) => listProjects(signal),
        staleTime: 60_000,
      });
      if (cancelled) return;
      const match = projects.find((p) => p.siteId === siteId);
      // Guard `match.siteId` explicitly — `UnifiedProject.siteId` is
      // `string | null`; the `find` predicate narrows it at runtime but not in
      // the type system. The early-return makes the invariant self-evident.
      if (!match || !match.siteId) return;
      dispatch(setWorkingSiteSuccess({
        workingSite: {
          siteId: match.siteId,
          trusstorProjectId: match.trusstorProjectId,
          name: match.name,
          tz: match.tz,
        },
      }));
    })();
    return () => { cancelled = true; };
  }, [siteId, workingSite?.siteId, dispatch, queryClient]);
}
